import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/apiClient";

export default function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { user, login } = useAuth();

  useEffect(() => {
    if (user && user !== false) navigate("/contact/admin/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back, officer");
      navigate("/contact/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1128] flex flex-col dvl-grain">
      <div className="p-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-300 text-sm"
          data-testid="back-to-site"
        >
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-lg border border-amber-500/40 bg-[#0F1C3F] flex items-center justify-center mx-auto mb-5">
              <Shield className="w-6 h-6 text-amber-400" />
            </div>
            <h1 className="font-serif text-3xl">Admin Portal</h1>
            <p className="text-slate-400 mt-2 text-sm">Authorised custodians only.</p>
          </div>

          <form
            onSubmit={submit}
            className="bg-[#0F1C3F]/80 border border-amber-500/25 rounded-2xl p-8 space-y-5"
            data-testid="admin-login-form"
          >
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-amber-400/80 mb-2 block">
                Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="admin-email"
                className="bg-slate-950/80 border-amber-500/30 text-slate-100 h-11"
              />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-amber-400/80 mb-2 block">
                Password
              </label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-password"
                className="bg-slate-950/80 border-amber-500/30 text-slate-100 h-11"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-semibold h-11"
            >
              {submitting ? "Verifying…" : "Enter portal"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
